import { useState } from "react";
import {
  Trash,
  PencilSimple,
  DownloadSimple,
  SpinnerGap,
} from "@phosphor-icons/react";
import type { ImageFile } from "@/App";
import { EditModal } from "@/components/EditModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

interface ImagesProps {
  images: ImageFile[];
  onDelete: (id: number) => void;
}

export function Images({ images, onDelete }: ImagesProps) {
  return (
    <div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {images.map((image) => {
          if (image.file.type.includes("video")) {
            return <Video video={image} key={image.id} />;
          }
          return (
            <ImageSpot image={image} onDelete={onDelete} key={image.id} />
          );
        })}
      </div>
    </div>
  );
}

function Video({ video }: { video: ImageFile }) {
  const url = URL.createObjectURL(video.file);
  return (
    <Card className="overflow-hidden p-3">
      <video
        className="aspect-square rounded-lg object-cover"
        loop
        muted
        autoPlay
        src={url}
      />
    </Card>
  );
}

interface ImageSpotProps {
  image: ImageFile;
  onDelete: (id: number) => void;
}

function ImageSpot({ image, onDelete }: ImageSpotProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState("");

  const url = URL.createObjectURL(image.file);
  const processedURL = image.processedFile
    ? URL.createObjectURL(image.processedFile)
    : "";
  const isProcessing = !image.processedFile;

  const handleEditSave = (editedImageUrl: string) => {
    setProcessedImageUrl(editedImageUrl);
  };

  const transparentBg = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="relative">
          {isProcessing ? (
            <div className="relative">
              <img
                className="aspect-square w-full object-cover opacity-50 transition-opacity duration-200"
                src={url}
                alt={`Processing image ${image.id}`}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-lg bg-black/50 px-4 py-2">
                  <SpinnerGap className="mr-2 inline-block size-4 animate-spin text-white" />
                  <span className="font-medium text-white">Processing...</span>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="aspect-square w-full"
              style={{
                background: transparentBg,
                backgroundRepeat: "repeat",
              }}
            >
              <img
                className="h-full w-full object-cover transition-opacity duration-200"
                src={processedImageUrl || processedURL}
                alt={`Processed image ${image.id}`}
              />
            </div>
          )}
        </div>
      </CardContent>

      {!isProcessing && (
        <CardFooter className="justify-center gap-2 border-t p-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(image.id)}
          >
            <Trash className="text-destructive" />
            Delete
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditModalOpen(true)}
          >
            <PencilSimple className="text-primary" />
            Edit
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a
              href={processedImageUrl || processedURL}
              download={`processed-${image.id}.png`}
            >
              <DownloadSimple className="text-green-600" />
              Download
            </a>
          </Button>
        </CardFooter>
      )}

      <EditModal
        image={image}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
    </Card>
  );
}
